// src/features/patient/care-treatment/components/CareTreatment.tsx

/**
 * Purpose : The Care & Treatment section: the "What we found" banner, summary cards,
 * visits as a timeline of visit days, calls, messages & letters, care plans,
 * the care team, and notes waiting for a visit. A visit day or a note
 * replaces the view, with a back button. A header search result opens a
 * note, visit, person or tab here (focus).
 *
 * Contents :
 *
 * * type(Open) : which screen replaces the tabs
 * * type(CareFocus) : what a header search result opens here
 * * function(CareTreatment) : summary cards, tabs, and which screen is open
 * * function(backLabelFor) : what a note's Back returns to (the back trail)
 * * function(LoadingState) : skeletons while the data loads
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { Link } from "react-router-dom";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { PillTabs } from "@/shared/components/PillTabs";
import { cn } from "@/shared/lib/utils";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import type {
  CarePlanOut, CareTeamMember, CareTimelineCounts, Encounter, IdentityPending, Note, OtherDocument, UploadSummary,
} from "@/features/patient/care-treatment/api/careTimelineTypes";
import { formatDateOnly, notesForVisit, notesWithoutVisit, searchTerms, summaryCounts } from "@/features/patient/care-treatment/utils/careTimeline";
import { recordTypeLabel } from "@/features/patient/care-treatment/utils/careNaming";
import { buildCareTeam, cleanCarePlans } from "@/features/patient/care-treatment/utils/careTeamPlans";
import {
  aroundVisit,
  dayMatches,
  dayNoteSequence,
  dayHasDetails,
  dayNotes,
  dayNotesMissing,
  groupVisitDays,
  neighbourDays,
  recordDay,
  type DayContent,
  type VisitDay,
  type VisitDayFilter,
} from "@/features/patient/care-treatment/utils/visitDays";
import { VisitDetailView } from "./VisitDetailView";
import { NoteViewerView } from "./NoteViewerView";
import { VisitsTimeline } from "./VisitsTimeline";
import { CallsTab } from "./CallsTab";
import { ReviewTab } from "./ReviewTab";
import { CarePlansTab, CareTeamTab } from "./CarePlansTeamTabs";
import {
  brandButtonClass,
  cardClass,
  faintTextClass,
  mutedIconClass,
  pageClass,
  redIconBoxClass,
  statCardClass,
  warningClass,
} from "./careStyles";

export type Tab = "visits" | "other" | "plans" | "team" | "review";

// type(Open)
/** What replaces the tabs: a visit day (or one record), or a note (remembering where it was opened from). */
export type Open =
  | { kind: "day"; id: string }
  | { kind: "note"; id: string; fromDayId: string | null; highlight?: string[] }
  | null;

// type(CareFocus)
/** A header search result: a note (its words marked), a visit, a person's visits, or a tab. */
export type CareFocus =
  | { kind: "note"; id: string; q?: string }
  | { kind: "visit"; id: string }
  | { kind: "person"; key: string }
  | { kind: "tab"; tab: Tab };

interface CareTreatmentProps {
  encounters: Encounter[];
  notes: Note[];
  /** Forms and unclassified parts of uploads not linked to any visit. */
  documents?: OtherDocument[];
  /** Scanned pages are still being read and the background checks have stopped. */
  stillProcessing?: boolean;
  /** The server's counts over everything (notes are paged); else counted from what's loaded. */
  counts?: CareTimelineCounts;
  /** Uploads waiting for "Yes, this is me": their records are left out until then. */
  identityPending?: IdentityPending | null;
  /** From the server; absent (not loaded) → built from visits and notes. */
  careTeam?: CareTeamMember[];
  carePlans?: CarePlanOut[];
  /** Older notes exist that aren't loaded yet; onLoadOlder fetches the next page. */
  hasOlder?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
  /** Fetch the page of notes that starts on this day (a visit whose notes aren't loaded yet). */
  onNeedNotesFor?: (day: string) => void;
  isLoading?: boolean;
  /** The first load failed: a message and Try again instead of "No visits yet". */
  isError?: boolean;
  onRetry?: () => void;
  /** Start on this tab / screen (the dev harness). */
  initialTab?: Tab;
  initialOpen?: Open;
  /** Open this (a header search result); a new key opens it again. A note not loaded yet opens once it is. */
  focus?: { focus: CareFocus; key: number };
}

// function(CareTreatment)
export function CareTreatment({
  encounters, notes, documents = [], stillProcessing = false, counts: serverCounts, identityPending,
  careTeam, carePlans, hasOlder = false, loadingOlder = false, onLoadOlder, onNeedNotesFor, isLoading = false, isError = false, onRetry,
  initialTab = "visits", initialOpen = null, focus,
}: CareTreatmentProps) {
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;
  const [tab, setTab] = useState<Tab>(initialTab);
  const [filter, setFilter] = useState<VisitDayFilter>("all");
  const [clinician, setClinician] = useState<string | null>(null);
  /** A summary card's narrowing: only days with doctors' notes, or with results & orders. */
  const [content, setContent] = useState<DayContent | null>(null);
  const [open, setOpen] = useState<Open>(initialOpen);
  /** The screens behind the open one, oldest first: Back returns to the last (a call opened from a visit goes back to that visit). */
  const [trail, setTrail] = useState<{ open: Open; scroll: number }[]>([]);
  /** Where to scroll once the screen Back returned to is drawn. */
  const restoreScroll = useRef<number | null>(null);
  const scroller = () => document.getElementById("main-content");
  /** Opens a screen and remembers where it was opened from, and how far down it was. */
  const go = (next: Open) => {
    setTrail((t) => [...t, { open, scroll: scroller()?.scrollTop ?? 0 }]);
    restoreScroll.current = 0;
    setOpen(next);
  };
  /** A new start (a header search result): no trail. From the list, the list is the trail. */
  const jump = (next: Open) => {
    setTrail(open === null && next !== null ? [{ open: null, scroll: scroller()?.scrollTop ?? 0 }] : []);
    restoreScroll.current = 0;
    setOpen(next);
  };
  useLayoutEffect(() => {
    if (restoreScroll.current === null) return;
    scroller()?.scrollTo?.({ top: restoreScroll.current });
    restoreScroll.current = null;
  }, [open]);

  const counts = useMemo(() => serverCounts ?? summaryCounts(encounters, notes), [serverCounts, encounters, notes]);
  const encounterById = useMemo(() => new Map(encounters.map((e) => [e.id, e])), [encounters]);
  const days = useMemo(() => groupVisitDays(encounters), [encounters]);
  const dayOfEncounter = useMemo(() => {
    const map = new Map<string, VisitDay>();
    days.forEach((d) => d.parts.forEach((p) => map.set(p.id, d)));
    return map;
  }, [days]);
  const team = useMemo(() => careTeam ?? buildCareTeam(encounters, notes), [careTeam, encounters, notes]);
  const realPlans = useMemo(() => cleanCarePlans(carePlans ?? []), [carePlans]);

  // A header search result: open it here (a note may arrive a moment later, when its page loads).
  const [pendingNote, setPendingNote] = useState<{ id: string; q?: string } | null>(null);
  useEffect(() => {
    // The page gave up on a result (its note isn't there): stop waiting for it.
    if (!focus) {
      setPendingNote(null);
      return;
    }
    const target = focus.focus;
    document.getElementById("main-content")?.scrollTo?.({ top: 0 });
    if (target.kind === "note") {
      setPendingNote({ id: target.id, q: target.q });
    } else if (target.kind === "visit") {
      const record = encounterById.get(target.id);
      const own = record ? notesForVisit(record, notes) : [];
      if (record?.group === "non_visit" && own.length === 1) jump({ kind: "note", id: own[0].id, fromDayId: null });
      else jump({ kind: "day", id: dayOfEncounter.get(target.id)?.id ?? target.id });
    } else if (target.kind === "person") {
      jump(null);
      setFilter("all");
      setContent(null);
      setClinician(target.key);
      setTab("visits");
    } else {
      jump(null);
      setTab(target.tab);
    }
    // Only a new request opens something; later data changes don't.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.key]);
  const pendingLoaded = pendingNote !== null && notes.some((n) => n.id === pendingNote.id);
  useEffect(() => {
    if (!pendingLoaded || !pendingNote) return;
    jump({ kind: "note", id: pendingNote.id, fromDayId: null, highlight: pendingNote.q ? searchTerms(pendingNote.q) : undefined });
    setPendingNote(null);
  }, [pendingLoaded, pendingNote]);

  // A visit opened whose notes are on a page not loaded yet: load that day's page.
  const openedDay = open?.kind === "day" ? days.find((d) => d.id === open.id) ?? dayOfEncounter.get(open.id) : undefined;
  const missingFor = openedDay && dayNotesMissing(openedDay, dayNotes(openedDay, notes)) ? openedDay.date : null;
  useEffect(() => {
    if (missingFor) onNeedNotesFor?.(missingFor);
    // Once per day opened; the loader ignores a day already fetched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingFor]);

  if (isLoading) return <LoadingState />;
  if (isError) {
    return (
      <div className={pageClass}>
        <EmptyState
          icon={<Icon icon="lucide:cloud-off" className={mutedIconClass} />}
          title="Couldn't load your visits and notes"
          description="Something went wrong on our side. Your records are safe; please try again."
          action={onRetry && <Button onClick={onRetry} className={brandButtonClass}>Try again</Button>}
        />
      </div>
    );
  }

  /** A visit day by its id, or one record's own page. */
  const findDay = (id: string): VisitDay | null => {
    const day = days.find((d) => d.id === id) ?? dayOfEncounter.get(id);
    if (day) return day;
    const record = encounterById.get(id);
    return record ? recordDay(record) : null;
  };
  const openNote = (note: Note, fromDayId: string | null) => go({ kind: "note", id: note.id, fromDayId });
  /** Back to the screen this one was opened from; with none, a note opened from a visit goes to it, else the list. */
  const back = () => {
    if (trail.length > 0) {
      const last = trail[trail.length - 1];
      restoreScroll.current = last.scroll;
      setOpen(last.open);
      setTrail(trail.slice(0, -1));
      return;
    }
    setOpen(open?.kind === "note" && open.fromDayId ? { kind: "day", id: open.fromDayId } : null);
  };
  // A call or letter with one note opens straight to it, as the list promises.
  const openRecord = (encounter: Encounter) => {
    const own = notesForVisit(encounter, notes);
    if (encounter.group === "non_visit" && own.length === 1) openNote(own[0], null);
    else go({ kind: "day", id: dayOfEncounter.get(encounter.id)?.id ?? encounter.id });
  };
  // The open screen, drawn over the list; the list stays mounted (hidden), so
  // Back finds it as it was: its year filter, view, "Load older" pages and scroll.
  let screen: ReactNode = null;
  if (open?.kind === "day") {
    const day = findDay(open.id);
    if (day) {
      const isVisit = day.parts[0].group === "visit";
      // Previous / next follow the timeline's filters; a day outside them walks the whole timeline.
      // Visits with nothing to open are skipped, as in the list (this one stays, to find its neighbours).
      const openable = (list: VisitDay[]) => list.filter((d) => d.id === day.id || dayHasDetails(d, dayNotes(d, notes)));
      const filtered = days.filter((d) => dayMatches(d, notes, filter, clinician, content));
      const { previous, next } = neighbourDays(openable(filtered.some((d) => d.id === day.id) ? filtered : days), day.id);
      screen = (
        <VisitDetailView
          key={day.id}
          day={day}
          notes={notes}
          around={isVisit ? aroundVisit(day, encounters) : []}
          plans={realPlans.filter((p) => p.encounterId && day.parts.some((part) => part.id === p.encounterId))}
          previous={isVisit ? previous : null}
          next={isVisit ? next : null}
          actingAsProfileId={actingAsProfileId}
          onBack={back}
          // Previous / next visit: the same place in the trail (Back still goes where this visit came from).
          onOpenDay={(d) => setOpen({ kind: "day", id: d.id })}
          onOpenNote={(note) => openNote(note, day.id)}
          onOpenRecord={openRecord}
        />
      );
    }
  }

  if (open?.kind === "note" && !screen) {
    const note = notes.find((n) => n.id === open.id);
    if (note) {
      const day = (open.fromDayId && findDay(open.fromDayId)) || (note.encounterId ? findDay(note.encounterId) : null);
      const encounter = (note.encounterId && encounterById.get(note.encounterId)) || day?.parts[0] || null;
      const sequence = day ? dayNoteSequence(day, notes) : [];
      const at = sequence.findIndex((n) => n.id === note.id);
      screen = (
        <NoteViewerView
          key={note.id}
          note={note}
          encounter={encounter}
          actingAsProfileId={actingAsProfileId}
          highlight={open.highlight}
          previous={at >= 0 ? sequence[at - 1] ?? null : undefined}
          next={at >= 0 ? sequence[at + 1] ?? null : undefined}
          // Previous / next note: the same place in the trail.
          onOpenNote={(n) => setOpen({ kind: "note", id: n.id, fromDayId: day?.id ?? null })}
          // Opened from this visit: the same as Back (no loop of visit, note, visit...).
          onOpenVisit={day ? () => {
            const last = trail[trail.length - 1]?.open;
            if (last?.kind === "day" && last.id === day.id) back();
            else go({ kind: "day", id: day.id });
          } : undefined}
          onBack={back}
          backLabel={backLabelFor(trail[trail.length - 1]?.open, day?.id)}
        />
      );
    }
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "visits", label: "Visits" },
    { id: "other", label: "Calls, messages & letters" },
    { id: "team", label: "Care team" },
    { id: "plans", label: "Care plans" },
    { id: "review", label: counts.toReview > 0 ? `To review (${counts.toReview})` : "To review" },
  ];

  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  // Each card opens what it counts: the visits, only the visits with doctors' notes or with results, the calls, To review.
  const stats: { icon: string; label: string; value: number; hint: string; tab: Tab; content?: DayContent }[] = [
    { icon: "lucide:calendar-days", label: "Visits", value: counts.visits, hint: `On ${plural(days.length, "day", "days")}`, tab: "visits" },
    { icon: "lucide:file-text", label: "Doctors’ notes", value: counts.clinicalNotes, hint: "See visits with notes", tab: "visits", content: "notes" },
    { icon: "lucide:file-chart-column", label: "Results & orders", value: counts.reports, hint: "See visits with results", tab: "visits", content: "results" },
    { icon: "lucide:phone", label: "Calls & messages", value: counts.callsAndMessages + notesWithoutVisit(notes).length, hint: "Phone calls, messages, letters", tab: "other" },
    {
      icon: "lucide:list-checks", label: "To review", value: counts.toReview, tab: "review",
      hint: counts.toReview > 0 ? "Notes to match to a visit" : "Nothing to check",
    },
  ];
  const pickStat = (target: Tab, narrow: DayContent | undefined) => {
    setTab(target);
    if (target === "visits") setContent(narrow ?? null);
  };

  return (
    <>
      {screen}
      <div className={pageClass} hidden={screen !== null}>

        {/* Summary — 5 across only when there's room (xl) */}
        <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 xl:grid-cols-5">
          {stats.map(({ icon, label, value, hint, tab: target, content: narrow }) => (
            <Button
              key={label}
              variant="outline"
              onClick={() => pickStat(target, narrow)}
              aria-pressed={tab === target && (target !== "visits" || (narrow ?? null) === content)}
              className={statCardClass}
            >
              <div className="flex min-w-0 items-center gap-2">
                <div className={cn(redIconBoxClass, "h-9 w-9 shrink-0 rounded-full")}>
                  <Icon icon={icon} className="h-4 w-4 !text-white" />
                </div>
                <p className="min-w-0 whitespace-normal text-sm font-semibold leading-tight text-[#111827]">{label}</p>
              </div>
              <p className="text-right text-3xl font-bold text-[#111827]">{value}</p>
              <p className={cn("!mb-0 whitespace-normal text-right", faintTextClass)}>{hint}</p>
            </Button>
          ))}
        </div>

        {identityPending && identityPending.count > 0 && (
          <div role="status" data-testid="identity-pending" className={cn("mb-5 flex flex-wrap items-center justify-between gap-3", warningClass)}>
            <p className="!mb-0">
              {identityPending.count === 1
                ? "1 upload is waiting for you to confirm it's yours. Its records appear here after you answer."
                : `${identityPending.count} uploads are waiting for you to confirm they're yours. Their records appear here after you answer.`}
            </p>
            <Link
              to={identityPending.reviewItemId
                ? `/patient/patient-only-queue?item=${encodeURIComponent(identityPending.reviewItemId)}`
                : "/patient/patient-only-queue"}
              className="text-sm font-bold text-[#0A2947] underline"
            >
              Review now
            </Link>
          </div>
        )}

        {stillProcessing && (
          <div role="status" className={cn("mb-5", warningClass)}>
            <p>
              <strong>Still processing:</strong> Some scanned pages are still being read. Check back later to see their text.
            </p>
          </div>
        )}

        <div className="mb-6 flex justify-center">
          <PillTabs tabs={tabs} active={tab} onChange={setTab} label="Care & Treatment views" />
        </div>

        {tab === "visits" && (
          <VisitsTimeline
            days={days}
            notes={notes}
            filter={filter}
            onFilter={setFilter}
            clinician={clinician}
            onClinician={setClinician}
            content={content}
            onClearContent={() => setContent(null)}
            people={team}
            onOpen={(day) => jump({ kind: "day", id: day.id })}
          />
        )}
        {tab === "other" && (
          <CallsTab
            encounters={encounters}
            notes={notes}
            onOpen={openRecord}
            looseNotes={notesWithoutVisit(notes)}
            onOpenNote={(note) => openNote(note, null)}
          />
        )}
        {tab === "plans" && (
          <CarePlansTab
            plans={realPlans}
            visitLabel={(id) => {
              const visit = encounterById.get(id);
              return visit ? [recordTypeLabel(visit), formatDateOnly(visit.start)].filter(Boolean).join(" · ") : null;
            }}
            onOpenVisit={(id) => go({ kind: "day", id: dayOfEncounter.get(id)?.id ?? id })}
          />
        )}
        {tab === "team" && (
          <CareTeamTab
            members={team}
            onPick={(member) => {
              setFilter("all");
              setContent(null);
              setClinician(member.key);
              setTab("visits");
            }}
          />
        )}
        {tab === "review" && (
          <ReviewTab
            notes={notes}
            encounterById={encounterById}
            onOpenNote={(note) => openNote(note, null)}
            actingAsProfileId={actingAsProfileId}
            total={counts.toReview}
            hasOlder={hasOlder}
            loadingOlder={loadingOlder}
            onLoadOlder={onLoadOlder}
          />
        )}
      </div>
    </>
  );
}

// function(LoadingState)
/** Same skeleton layout as the current Care & Treatment view. */
function LoadingState() {
  return (
    <div className={pageClass}>
      <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 xl:grid-cols-5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="min-h-[110px] rounded-3xl border border-[#E5E7EB] bg-white px-6 py-5 shadow-sm">
            <Skeleton active paragraph={{ rows: 1 }} />
          </div>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={cardClass}>
            <Skeleton active avatar paragraph={{ rows: 2 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// function(backLabelFor)
/** What a note's Back returns to, when it isn't the note's own visit (the default label). */
function backLabelFor(previous: Open | undefined, ownDayId: string | undefined): string | undefined {
  if (previous === undefined) return undefined;
  if (previous === null) return "Care & Treatment";
  if (previous.kind === "day" && previous.id !== ownDayId) return "the visit you came from";
  if (previous.kind === "note") return "the previous note";
  return undefined;
}
